import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

/**
 * Opt-in wheel → horizontal scrolling for a horizontal-only ScrollArea.
 *
 * A plain mouse emits deltaY only, and the browser never applies that to an element that can
 * scroll sideways only — so a horizontal bar just sits there while the wheel scrolls something
 * behind it. This maps a predominantly vertical wheel onto scrollLeft.
 *
 * Opt-in rather than automatic, because "vertical wheel scrolls this sideways" is wrong wherever
 * something else legitimately wants the gesture: a wide table inside the chat (the reader means to
 * scroll the conversation) or a board whose columns scroll vertically inside it (the inner list
 * consumes the wheel and the event still bubbles here, so the board would scroll too).
 *
 * Left alone deliberately: trackpad sideways gestures (deltaX dominant) and shift+wheel already
 * scroll horizontally by themselves, and ctrl/cmd+wheel is a zoom gesture. The event is swallowed
 * ONLY when this viewport actually moved, so an exhausted bar still hands the wheel to whatever
 * scrolls behind it. The listener must be native and non-passive — React's own onWheel is
 * registered passive, where preventDefault is a no-op.
 */
function useWheelToHorizontalScroll(viewportRef, enabled) {
  React.useEffect(() => {
    const viewport = enabled ? viewportRef.current : null;
    if (!viewport) return undefined;
    const onWheel = (event) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = viewport.scrollWidth - viewport.clientWidth;
      if (max <= 0) return;
      // deltaMode: 0 = pixels, 1 = lines, 2 = pages.
      const step = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientWidth : 1;
      const next = Math.max(0, Math.min(max, viewport.scrollLeft + event.deltaY * step));
      if (next === viewport.scrollLeft) return;
      viewport.scrollLeft = next;
      event.preventDefault();
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [viewportRef, enabled]);
}

const ScrollArea = React.forwardRef(
  ({ className, children, viewportRef, viewportClassName, orientation = "vertical", wheelToHorizontal = false, ...props }, ref) => {
    // "both" renders both scrollbars and lets the viewport size to its content
    // in both axes — needed for grids that overflow horizontally (e.g. day view
    // with many tenant columns at a fixed min-width).
    const isBoth = orientation === "both";
    const isHorizontal = orientation === "horizontal";
    // Own handle on the viewport node for the wheel listener, kept alongside whatever ref the
    // caller passed (they get the same node, set the same way an object/callback ref would be).
    const localViewportRef = React.useRef(null);
    const setViewportRef = React.useCallback(
      (node) => {
        localViewportRef.current = node;
        if (typeof viewportRef === "function") viewportRef(node);
        else if (viewportRef && typeof viewportRef === "object") viewportRef.current = node;
      },
      [viewportRef],
    );
    useWheelToHorizontalScroll(localViewportRef, wheelToHorizontal && isHorizontal);
    return (
      <ScrollAreaPrimitive.Root ref={ref} className={cn("relative overflow-hidden", className)} {...props}>
        <ScrollAreaPrimitive.Viewport
          ref={setViewportRef}
          className={cn(
            "rounded-[inherit]",
            isBoth && "size-full",
            !isBoth && (isHorizontal ? "w-full max-w-full" : "h-full w-full *:first:block!"),
            // For max-height scroll areas (menus/popovers) the bound must sit on the
            // VIEWPORT — a max-h on the auto-height Root never constrains it.
            viewportClassName,
          )}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {isBoth ? (
          <>
            <ScrollBar orientation="vertical" />
            <ScrollBar orientation="horizontal" />
          </>
        ) : (
          <ScrollBar orientation={isHorizontal ? "horizontal" : "vertical"} />
        )}
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-px",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-px",
      className
    )}
    {...props}>
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
